import { ReactNode } from "react";
import { TooltipTrigger } from "react-aria-components";
import { UnstyledRACButton } from "../../buttons/UnstyledRACButton";
import { TooltipBubble } from "../TooltipBubble";
import { infoTextButton } from "./infoText.css";

interface InfoTextProps {
  /**
   * The accessible label for screen readers (used for aria-label).
   * This is what screen readers will reliably announce.
   */
  label: string;
  /**
   * Optional text for the tooltip bubble. Only provide this if it adds information beyond `label`.
   * If omitted, the tooltip will show `label` visually but is hidden from screen readers to
   * prevent redundant announcements. Ensure `label` contains all essential information.
   */
  tooltipText?: string;
  /**
   * The text to display (can be a symbol, full word, or abbreviation).
   */
  children: ReactNode;
}

/**
 * Renders text with a tooltip and visual indicator (dotted underline).
 *
 * **When to use:**
 * - Any text that needs additional context or explanation via a tooltip
 *
 * **Accessibility:**
 * - Provides the accessible name via `aria-label={label}` - this is always announced
 * - Provide `tooltipText` only when it adds information beyond `label` (e.g., extra context or explanation)
 * - If `tooltipText` is omitted, the tooltip shows `label` visually but is hidden from screen readers
 * - Ensure `label` contains all essential information for screen reader users
 */
const InfoText = (props: InfoTextProps) => {
  const { label, tooltipText, children, ...buttonProps } = props;
  return (
    <TooltipTrigger delay={100}>
      <UnstyledRACButton
        {...buttonProps}
        className={infoTextButton}
        aria-label={label}
      >
        {children}
      </UnstyledRACButton>
      <TooltipBubble>
        {tooltipText && tooltipText !== label ? (
          tooltipText
        ) : (
          <span aria-hidden="true">{label}</span>
        )}
      </TooltipBubble>
    </TooltipTrigger>
  );
};

export default InfoText;
